import React, { useState } from 'react';
import {
  ArrowLeft,
  Shield,
  Lock,
  Mail,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Trash2,
  KeyRound,
  Chrome,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../lib/utils';

type ToastState = { message: string; type: 'success' | 'error' } | null;

function Toast({ toast, onClose }: { toast: ToastState; onClose: () => void }) {
  if (!toast) return null;
  return (
    <div className={cn(
      'fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center space-x-2 px-5 py-3 rounded-xl shadow-lg text-sm font-medium max-w-sm',
      toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
    )}>
      {toast.type === 'success' ? <CheckCircle2 className="h-4 w-4 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
      <span>{toast.message}</span>
      <button onClick={onClose} className="ml-2 text-current opacity-50 hover:opacity-100">&times;</button>
    </div>
  );
}

export function SecurityPage() {
  const navigate = useNavigate();
  const { user, changePassword, sendVerificationEmail, deleteAccount, signOut } = useAuth();

  const [toast, setToast] = useState<ToastState>(null);
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Delete account
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Verification
  const [sendingVerification, setSendingVerification] = useState(false);

  const hasPasswordProvider = user?.providerData.some(p => p.providerId === 'password') ?? false;
  const hasGoogleProvider = user?.providerData.some(p => p.providerId === 'google.com') ?? false;

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      showToast('New password must be at least 6 characters', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }
    try {
      setChangingPassword(true);
      await changePassword(currentPassword, newPassword);
      showToast('Password updated successfully', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const msg = err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential'
        ? 'Current password is incorrect'
        : err.message || 'Failed to change password';
      showToast(msg, 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSendVerification = async () => {
    try {
      setSendingVerification(true);
      await sendVerificationEmail();
      showToast('Verification email sent! Check your inbox.', 'success');
    } catch (err: any) {
      showToast(err.message || 'Failed to send verification email', 'error');
    } finally {
      setSendingVerification(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      showToast('Please type DELETE to confirm', 'error');
      return;
    }
    try {
      setDeleting(true);
      await deleteAccount(deletePassword);
      navigate('/signin');
    } catch (err: any) {
      const msg = err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential'
        ? 'Password is incorrect'
        : err.message || 'Failed to delete account';
      showToast(msg, 'error');
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center">
            <button onClick={() => navigate('/settings', { replace: true })} className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors">
              <ArrowLeft className="h-6 w-6 text-heading" />
            </button>
            <h1 className="ml-4 text-xl font-serif text-heading">Security</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6 pb-24">
        {/* Email Verification */}
        <div>
          <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
            Email Verification
          </h2>
          <Card>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center',
                    user?.emailVerified ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'
                  )}>
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium text-heading">{user?.email}</p>
                    <p className={cn('text-sm', user?.emailVerified ? 'text-green-600' : 'text-amber-600')}>
                      {user?.emailVerified ? 'Verified' : 'Not verified'}
                    </p>
                  </div>
                </div>
                {!user?.emailVerified && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSendVerification}
                    disabled={sendingVerification}
                  >
                    {sendingVerification ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
                  </Button>
                )}
                {user?.emailVerified && (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Connected Sign-in Methods */}
        <div>
          <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
            Sign-in Methods
          </h2>
          <Card>
            <CardContent className="p-0 divide-y divide-gray-100">
              {hasPasswordProvider && (
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                      <KeyRound className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-heading">Email & Password</p>
                      <p className="text-sm text-body">Sign in with your email and password</p>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">Active</span>
                </div>
              )}
              {hasGoogleProvider && (
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                      <Chrome className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium text-heading">Google</p>
                      <p className="text-sm text-body">Sign in with your Google account</p>
                    </div>
                  </div>
                  <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">Connected</span>
                </div>
              )}
              {!hasPasswordProvider && !hasGoogleProvider && (
                <div className="p-4 text-center text-sm text-body">
                  No sign-in methods linked
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Change Password */}
        {hasPasswordProvider && (
          <div>
            <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
              Change Password
            </h2>
            <Card>
              <CardContent className="p-5">
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-heading mb-1.5">Current Password</label>
                    <div className="relative">
                      <Input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={e => setCurrentPassword(e.target.value)}
                        placeholder="Enter current password"
                        required
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-heading mb-1.5">New Password</label>
                    <div className="relative">
                      <Input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        placeholder="At least 6 characters"
                        required
                        minLength={6}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {newPassword.length > 0 && newPassword.length < 6 && (
                      <p className="text-xs text-amber-600 mt-1">Password must be at least 6 characters</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-heading mb-1.5">Confirm New Password</label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter new password"
                      required
                      autoComplete="new-password"
                      error={confirmPassword.length > 0 && confirmPassword !== newPassword}
                    />
                    {confirmPassword.length > 0 && confirmPassword !== newPassword && (
                      <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    loading={changingPassword}
                    disabled={!currentPassword || !newPassword || newPassword !== confirmPassword || newPassword.length < 6}
                    className="w-full"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    Update Password
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Delete Account */}
        <div>
          <h2 className="text-sm font-medium text-body uppercase tracking-wider mb-3 px-1">
            Danger Zone
          </h2>
          <Card className="border-red-200">
            <CardContent className="p-5">
              {!showDeleteConfirm ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                      <Trash2 className="h-5 w-5 text-red-500" />
                    </div>
                    <div>
                      <p className="font-medium text-heading">Delete Account</p>
                      <p className="text-sm text-body">Permanently remove your account and all data</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    Delete
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-red-900 text-sm">This action is permanent</p>
                      <p className="text-sm text-red-700 mt-1">
                        All your data, order history, and saved preferences will be permanently deleted. This cannot be undone.
                      </p>
                    </div>
                  </div>
                  {hasPasswordProvider && (
                    <div>
                      <label className="block text-sm font-medium text-heading mb-1.5">Enter your password</label>
                      <Input
                        type="password"
                        value={deletePassword}
                        onChange={e => setDeletePassword(e.target.value)}
                        placeholder="Your current password"
                        autoComplete="current-password"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-heading mb-1.5">
                      Type <strong>DELETE</strong> to confirm
                    </label>
                    <Input
                      type="text"
                      value={deleteConfirmText}
                      onChange={e => setDeleteConfirmText(e.target.value)}
                      placeholder="DELETE"
                    />
                  </div>
                  <div className="flex space-x-3">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeletePassword('');
                        setDeleteConfirmText('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      loading={deleting}
                      disabled={deleteConfirmText !== 'DELETE' || (hasPasswordProvider && !deletePassword)}
                      onClick={handleDeleteAccount}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Account
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
